import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface CollapsibleSectionHeaderProps {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  /** Id of the element this header shows and hides. */
  controlsId: string;
  /** Active selections, shown beside the title only while collapsed. */
  activeCount?: number;
  /** Extra header controls (e.g. Clear) that stay visible when collapsed. */
  action?: ReactNode;
  className?: string;
}

/**
 * A desktop filter-sidebar section title that folds the section's body away.
 * Shared by [[records-sidebar.spec]] and [[issue-filter-sidebar.spec]]. See
 * [[collapsible-section.spec]].
 */
export function CollapsibleSectionHeader({
  title,
  collapsed,
  onToggle,
  controlsId,
  activeCount = 0,
  action,
  className,
}: CollapsibleSectionHeaderProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <h3 className="min-w-0 flex-1">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-controls={controlsId}
          className="-my-1 flex w-full cursor-pointer items-center gap-1.5 rounded-sm py-1 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="truncate">{title}</span>
          {collapsed && activeCount > 0 && (
            <span className="rounded-full bg-tertiary px-1.5 text-[10px] leading-4 tracking-normal text-tertiary-foreground">
              <span className="sr-only">, active: </span>
              {activeCount}
            </span>
          )}
          <ChevronDown
            aria-hidden
            className={cn(
              "ml-auto h-3.5 w-3.5 shrink-0 transition-transform",
              collapsed && "-rotate-90",
            )}
          />
        </button>
      </h3>
      {action}
    </div>
  );
}
